import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { SessionUser } from "../shared/types";
import { api } from "./api";

interface AuthState {
  user: SessionUser | null;
  impersonator: SessionUser | null;
  loading: boolean;
  login(username: string, password: string): Promise<void>;
  signup(username: string, password: string, displayName: string): Promise<void>;
  logout(): Promise<void>;
  impersonate(userId: string): Promise<void>;
  stopImpersonating(): Promise<void>;
  setUser(u: SessionUser | null): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [impersonator, setImpersonator] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .me()
      .then((r) => {
        setUser(r.user);
        setImpersonator(r.impersonator ?? null);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const value: AuthState = {
    user,
    impersonator,
    loading,
    async login(username, password) {
      const r = await api.login(username, password);
      setUser(r.user);
    },
    async signup(username, password, displayName) {
      const r = await api.signup(username, password, displayName);
      setUser(r.user);
    },
    async logout() {
      await api.logout().catch(() => {});
      setUser(null);
      setImpersonator(null);
    },
    async impersonate(userId) {
      await api.impersonate(userId);
      const m = await api.me();
      setUser(m.user);
      setImpersonator(m.impersonator ?? null);
    },
    async stopImpersonating() {
      const r = await api.stopImpersonating();
      setUser(r.user);
      setImpersonator(null);
    },
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
